import { Button } from "@/components/ui/button"
import { CheckCircle, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useLanguage } from "@/lib/i18n"

export default function ExperienceSection() {
  const { t } = useLanguage()

  const milestones = [
    { year: "2015", eventKey: "experience.milestone1" },
    { year: "2018", eventKey: "experience.milestone2" },
    { year: "2021", eventKey: "experience.milestone3" },
    { year: "2024", eventKey: "experience.milestone4" },
  ]

  const values = [
    "experience.value1",
    "experience.value2",
    "experience.value3",
    "experience.value4",
    "experience.value5",
    "experience.value6",
  ]

  return (
    <section id="experience" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image and timeline */}
          <div className="relative">
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-xl border border-border">
              <img src="/images/image.png" alt="Reyes Trux Corp Fleet" className="object-cover w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            </div>

            {/* Timeline overlay with updated styling */}
            <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-sm border border-primary/30 rounded-lg p-4">
              <p className="text-xs text-primary font-semibold mb-3">{t("experience.journey")}</p>
              <div className="space-y-2">
                {milestones.slice(0, 3).map((milestone) => (
                  <div key={milestone.year} className="flex items-start gap-3">
                    <span className="text-primary font-bold text-sm">{milestone.year}</span>
                    <span className="text-xs text-muted-foreground">{t(milestone.eventKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            <div>
              <p className="text-primary font-semibold tracking-wider uppercase mb-4">{t("experience.tagline")}</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">{t("experience.title")}</h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">{t("experience.desc1")}</p>
              <p className="text-muted-foreground leading-relaxed">{t("experience.desc2")}</p>
            </div>

            {/* Values list */}
            <div className="grid sm:grid-cols-2 gap-4">
              {values.map((valueKey) => (
                <div key={valueKey} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{t(valueKey)}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild className="group">
                <Link to="#contact">
                  {t("experience.learnMore")}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="border-primary/50 hover:bg-primary/10 bg-transparent"
              >
                <Link to="#drivers">{t("experience.joinTeam")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
